import { Fragment, useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronRight, RefreshCw, Search, ShieldCheck, XCircle } from 'lucide-react';
import { Topbar } from '@/components/Topbar';
import { Button } from '@/components/Button';
import { Input } from '@/components/Input';
import { Select } from '@/components/Select';
import { dateTime, LoadState, Pager, tableClass } from '@/components/ControlCenter';
import { controlCenterService } from '@/services/control-center.service';
import { auditLogService, AuditQuery } from '@/services/audit-log.service';
import { useAdminResource } from '@/hooks/useAdminResource';

const PERIODS = [
  { value: '1', label: 'Last 24 hours' }, { value: '7', label: 'Last 7 days' }, { value: '30', label: 'Last 30 days' }, { value: '', label: 'All time' },
];

/** `whatsapp_account.default_changed` → "Whatsapp account · default changed". */
const pretty = (action: string) => {
  const [head, ...rest] = action.split('.');
  return `${head.replace(/_/g, ' ')}${rest.length ? ` · ${rest.join('.').replace(/_/g, ' ')}` : ''}`;
};

export default function AuditLogPage() {
  const [filters, setFilters] = useState<AuditQuery & { days: string }>({ page: 1, days: '7', outcome: '' });
  const [search, setSearch] = useState('');
  const [companySearch, setCompanySearch] = useState('');
  const [open, setOpen] = useState<string | null>(null);
  useEffect(() => {
    const timer = window.setTimeout(() => setFilters(prev => (prev.q === search ? prev : { ...prev, q: search || undefined, page: 1 })), 300);
    return () => window.clearTimeout(timer);
  }, [search]);
  const { data: companies } = useAdminResource(useCallback(() => controlCenterService.usageCompanies(companySearch), [companySearch]));
  const { data, loading, error, refresh } = useAdminResource(useCallback(() => {
    const { days, ...query } = filters;
    return auditLogService.list({
      ...query, limit: 50, outcome: query.outcome || undefined,
      since: days ? new Date(Date.now() - Number(days) * 864e5).toISOString() : undefined,
    });
  }, [filters]));
  const change = (patch: Partial<AuditQuery & { days: string }>) => setFilters(prev => ({ ...prev, ...patch, page: 1 }));

  return <>
    <Topbar title="Audit log" breadcrumb="Platform / Audit Log"
      right={<Button variant="ghost" size="sm" loading={loading} onClick={refresh} iconLeft={<RefreshCw size={14} />}>Refresh</Button>} />
    <div className="admin-page space-y-4">
      <section className="admin-panel flex flex-wrap items-center gap-2 p-3" aria-label="Audit filters">
        <div className="min-w-[14rem] flex-1">
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search action or record ID" iconLeft={<Search size={14} />} />
        </div>
        <Select value={filters.category ?? ''} onChange={e => change({ category: e.target.value || undefined })} className="!w-auto" aria-label="Category">
          <option value="">All categories</option>
          {data?.categories.map(category => <option key={category.key} value={category.key}>{category.label}</option>)}
        </Select>
        <div className="flex items-center gap-1">
          <Input value={companySearch} onChange={e => setCompanySearch(e.target.value)} placeholder="Find company" className="!w-36" aria-label="Find company" />
          <Select value={filters.companyUid ?? ''} onChange={e => change({ companyUid: e.target.value || undefined })} className="!w-auto" aria-label="Company">
            <option value="">All companies</option>
            {companies?.map(company => <option key={company.companyUid} value={company.companyUid}>{company.name}</option>)}
          </Select>
        </div>
        <Select value={filters.outcome ?? ''} onChange={e => change({ outcome: e.target.value as AuditQuery['outcome'] })} className="!w-auto" aria-label="Outcome">
          <option value="">Any outcome</option>
          <option value="succeeded">Succeeded</option>
          <option value="failed">Refused / failed</option>
        </Select>
        <Select value={filters.days} onChange={e => change({ days: e.target.value })} className="!w-auto" aria-label="Period">
          {PERIODS.map(period => <option key={period.value} value={period.value}>{period.label}</option>)}
        </Select>
      </section>

      <LoadState loading={loading} error={error} hasData={!!data} retry={refresh} />
      {data && <section className="admin-panel overflow-hidden">
        <div className="overflow-x-auto"><table className={tableClass}>
          <thead className="bg-surface-alt"><tr><th /><th>When</th><th>Actor</th><th>Company</th><th>Action</th><th>Record</th><th>Outcome</th></tr></thead>
          <tbody>{data.items.map(entry => {
            const expanded = open === entry._id;
            return <Fragment key={entry._id}>
              <tr className="cursor-pointer" onClick={() => setOpen(expanded ? null : entry._id)}>
                <td className="w-6 text-text-muted">{expanded ? <ChevronDown size={13} /> : <ChevronRight size={13} />}</td>
                <td className="text-text-muted">{dateTime(entry.createdAt)}</td>
                <td><span className="block font-medium">{entry.actorName ?? entry.actorUid}</span><span className="text-[11px] text-text-muted">{entry.actorRole.replace(/_/g, ' ')}</span></td>
                <td>{entry.companyUid ? <Link to={`/companies/${entry.companyUid}`} onClick={e => e.stopPropagation()} className="hover:text-primary">{entry.companyName ?? entry.companyUid}</Link> : <span className="text-text-muted">Platform</span>}</td>
                <td><span className="block capitalize">{pretty(entry.action)}</span><span className="font-mono text-[10px] text-text-muted">{entry.action}</span></td>
                <td className="max-w-[12rem] truncate text-text-muted">{entry.targetType.replace(/_/g, ' ')}{entry.targetUid ? ` · ${entry.targetUid}` : ''}</td>
                <td>{entry.outcome === 'failed'
                  ? <span className="flex items-center gap-1 text-danger"><XCircle size={12} />Refused{entry.statusCode ? ` (${entry.statusCode})` : ''}</span>
                  : <span className="flex items-center gap-1 text-success"><ShieldCheck size={12} />Done</span>}</td>
              </tr>
              {expanded && <tr><td colSpan={7} className="bg-surface-alt/50">
                <div className="grid gap-3 whitespace-normal py-2 text-[11px] md:grid-cols-3">
                  <div className="space-y-1">
                    <p><span className="text-text-muted">Request:</span> <span className="font-mono">{entry.method ?? '—'} {entry.route ?? ''}</span></p>
                    <p><span className="text-text-muted">IP:</span> {entry.ip ?? 'Not recorded'}</p>
                    {entry.params && <p><span className="text-text-muted">Route values:</span> <span className="font-mono">{Object.entries(entry.params).map(([k, v]) => `${k}=${v}`).join(', ')}</span></p>}
                  </div>
                  <div className="md:col-span-2">
                    <p className="mb-1 text-text-muted">Submitted fields (secrets redacted, long values shortened)</p>
                    <pre className="max-h-56 overflow-auto rounded-lg bg-surface p-2 font-mono text-[10px] leading-relaxed">{JSON.stringify(entry.payload ?? {}, null, 2)}</pre>
                  </div>
                </div>
              </td></tr>}
            </Fragment>;
          })}</tbody>
        </table></div>
        {!data.items.length && <p className="p-8 text-center text-sm text-text-muted">No audit entries match these filters.</p>}
        <Pager page={data.page} pageSize={data.limit} total={data.total} loading={loading} onChange={page => setFilters(prev => ({ ...prev, page }))} />
      </section>}
      <p className="text-xs text-text-muted">Entries are written for audited changes, including refused attempts. Passwords, keys, tokens, codes and PINs are never stored.</p>
    </div>
  </>;
}
